import React from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ReferenceDot,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DashboardData, DateRange } from '../../../../common/hooks/use-analytics-api.hook';
import { typography } from '../../../../components/design-system/theme';

// A4 at 96dpi — each page is captured as one PDF page
const PAGE_W = 794;
const PAGE_H = 1123;
const PAD = 48;
const CONTENT_W = PAGE_W - PAD * 2;

// Fixed print palette (always light, independent of app theme)
const ink = '#111827';
const inkSecondary = '#6B7280';
const inkMuted = '#9CA3AF';
const border = '#E5E7EB';
const headerBg = '#F9FAFB';
const accent = '#3B82F6';
const up = '#16A34A';
const down = '#DC2626';

const fontFamily = typography.fontFamily;

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    width: PAGE_W,
    height: PAGE_H,
    padding: PAD,
    paddingBottom: PAD + 24,
    backgroundColor: '#FFFFFF',
    fontFamily,
    color: ink,
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: ink,
    margin: '0 0 4px 0',
  },
  sectionSubtitle: {
    fontSize: 11,
    color: inkSecondary,
    margin: '0 0 14px 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 11,
  },
  th: {
    textAlign: 'left' as const,
    padding: '7px 10px',
    backgroundColor: headerBg,
    color: inkSecondary,
    fontWeight: 600,
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${border}`,
  },
  thNum: {
    textAlign: 'right' as const,
  },
  td: {
    padding: '7px 10px',
    borderBottom: `1px solid ${border}`,
    color: ink,
  },
  tdNum: {
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
};

const axisTick = { fontSize: 10, fill: inkSecondary, fontFamily };

const formatNumber = (n: number) => (n || 0).toLocaleString();

const Delta: React.FC<{ change: number }> = ({ change }) => {
  if (!change) {
    return <span style={{ fontSize: 11, color: inkMuted }}>—</span>;
  }
  const positive = change > 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: positive ? up : down }}>
      {positive ? '▲' : '▼'} {positive ? '+' : ''}{change.toFixed(1)}%
    </span>
  );
};

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div style={{ marginBottom: 28 }}>
    <p style={styles.sectionTitle}>{title}</p>
    {subtitle && <p style={styles.sectionSubtitle}>{subtitle}</p>}
    {children}
  </div>
);

// Aggregate daily points so long ranges stay readable: over ~5 months → monthly
// buckets, over ~2 months → weekly buckets, otherwise daily. Labels include the
// year on monthly buckets so a full-year range reads unambiguously.
const bucketSeries = (series: { date: string; value: number }[]): { date: string; value: number }[] => {
  const sumBy = (keyOf: (d: string) => string, labelOf: (k: string) => string) => {
    const buckets = new Map<string, number>();
    series.forEach((item) => {
      const k = keyOf(item.date);
      buckets.set(k, (buckets.get(k) || 0) + item.value);
    });
    return Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, value]) => ({ date: labelOf(k), value }));
  };

  if (series.length > 150) {
    return sumBy(
      (d) => dayjs(d).format('YYYY-MM'),
      (k) => dayjs(`${k}-01`).format('MMM YYYY')
    );
  }
  if (series.length > 60) {
    return sumBy(
      (d) => dayjs(d).startOf('week').format('YYYY-MM-DD'),
      (k) => dayjs(k).format('MMM DD')
    );
  }
  return series
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((item) => ({ date: dayjs(item.date).format('MMM DD'), value: item.value }));
};

const TrendChart: React.FC<{ series: { date: string; value: number }[]; gradientId: string }> = ({ series, gradientId }) => {
  const data = bucketSeries(series);
  const last = data[data.length - 1];
  const tickInterval = Math.max(0, Math.ceil(data.length / 8) - 1);

  return (
    <AreaChart width={CONTENT_W} height={230} data={data} margin={{ top: 16, right: 40, bottom: 0, left: 0 }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity={0.14} />
          <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke={border} strokeWidth={1} vertical={false} />
      <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={{ stroke: border }} interval={tickInterval} />
      <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v: number) => v.toLocaleString()} width={48} allowDecimals={false} />
      <Area
        type="monotone"
        dataKey="value"
        stroke={accent}
        strokeWidth={2}
        fill={`url(#${gradientId})`}
        isAnimationActive={false}
        dot={false}
      />
      {last && (
        <ReferenceDot
          x={last.date}
          y={last.value}
          r={4}
          fill={accent}
          stroke="#FFFFFF"
          strokeWidth={2}
          label={{ value: last.value.toLocaleString(), position: 'top', fontSize: 11, fill: ink, fontFamily, fontWeight: 600 }}
        />
      )}
    </AreaChart>
  );
};

export interface AnalyticsPdfReportProps {
  data: DashboardData;
  dateRange: DateRange;
  /** Prefetched product thumbnails as data URLs, keyed by productId (html2canvas cannot capture cross-origin images) */
  thumbnails?: Record<string, string>;
}

const ProductThumb: React.FC<{ src?: string; name: string }> = ({ src, name }) => {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={28}
        height={28}
        style={{ borderRadius: 4, objectFit: 'cover', border: `1px solid ${border}`, display: 'block' }}
      />
    );
  }
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 4,
        backgroundColor: headerBg,
        border: `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        color: inkSecondary,
      }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

export const AnalyticsPdfReport: React.FC<AnalyticsPdfReportProps> = ({ data, dateRange, thumbnails }) => {
  const { t, i18n } = useTranslation();
  const key = (suffix: string) => t(`ANALYTICS.MODERN_ANALYTICS.EXPORT_REPORT.${suffix}`);

  const period = `${dayjs(dateRange.startDate).format('MMM DD, YYYY')} – ${dayjs(dateRange.endDate).format('MMM DD, YYYY')}`;
  const generatedOn = dayjs().format('MMM DD, YYYY HH:mm');

  const overview = data.overview;
  const avgSessionSeconds = overview?.avgSessionDuration?.value || 0;
  const kpis = [
    { label: t('ANALYTICS.MODERN_ANALYTICS.TOTAL_VISITS'), value: formatNumber(overview?.totalVisits?.value), change: overview?.totalVisits?.change || 0 },
    { label: t('ANALYTICS.MODERN_ANALYTICS.ENGAGEMENTS'), value: formatNumber(overview?.totalEngagements?.value), change: overview?.totalEngagements?.change || 0 },
    { label: t('ANALYTICS.MODERN_ANALYTICS.ACTIVE_PRODUCTS'), value: formatNumber(overview?.activeProducts?.value), change: overview?.activeProducts?.change || 0 },
    { label: t('ANALYTICS.MODERN_ANALYTICS.CONVERSION_RATE'), value: `${(overview?.conversionRate?.value || 0).toFixed(2)}%`, change: overview?.conversionRate?.change || 0 },
    { label: t('ANALYTICS.MODERN_ANALYTICS.AVG_SESSION'), value: `${Math.floor(avgSessionSeconds / 60)}m ${Math.round(avgSessionSeconds % 60)}s`, change: overview?.avgSessionDuration?.change || 0 },
  ];

  const engagementRows = data.engagementBreakdown
    ? [
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.SPACE_SELECTION'), value: data.engagementBreakdown.spaceSelection || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.PRODUCT_CHANGE'), value: data.engagementBreakdown.productChange || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.UPLOADS'), value: data.engagementBreakdown.uploads || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.SEARCH'), value: data.engagementBreakdown.search || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.ROTATION'), value: data.engagementBreakdown.rotation || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.COMPARE'), value: data.engagementBreakdown.compare || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.DOWNLOAD'), value: data.engagementBreakdown.download || 0 },
      ].sort((a, b) => b.value - a.value)
    : [];
  const engagementTotal = engagementRows.reduce((sum, row) => sum + row.value, 0);

  const visitsTotal = (data.trends?.visits || []).reduce((sum, item) => sum + item.value, 0);
  const engagementsTrendTotal = (data.trends?.engagements || []).reduce((sum, item) => sum + item.value, 0);

  const journeyStages = data.userJourney?.stages || [];
  const topCountries = data.geographicMetrics?.topCountries || [];
  const topCities = data.geographicMetrics?.topCities || [];
  const topProducts = (data.topProducts || []).slice(0, 10);

  // ── Page shell ──────────────────────────────────────────────────────────────
  const renderPage = (content: React.ReactNode, pageIndex: number, pageCount: number) => (
    <div key={pageIndex} data-pdf-page style={styles.page} dir={i18n.dir()}>
      {content}
      <div
        style={{
          position: 'absolute',
          left: PAD,
          right: PAD,
          bottom: 24,
          borderTop: `1px solid ${border}`,
          paddingTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 9,
          color: inkMuted,
        }}
      >
        <span>{key('REPORT_TITLE')} — {period}</span>
        <span>{key('PAGE')} {pageIndex + 1}/{pageCount}</span>
      </div>
    </div>
  );

  // ── Page 1: header + KPIs + trends ──────────────────────────────────────────
  const pageOne = (
    <>
      <div style={{ borderBottom: `2px solid ${ink}`, paddingBottom: 18, marginBottom: 24 }}>
        <p style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>{key('REPORT_TITLE')}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 12, color: inkSecondary }}>
            {key('PERIOD')}: <span style={{ color: ink, fontWeight: 600 }}>{period}</span>
          </span>
          <span style={{ fontSize: 12, color: inkSecondary }}>
            {key('GENERATED_ON')}: <span style={{ color: ink }}>{generatedOn}</span>
          </span>
        </div>
      </div>

      <Section title={t('ANALYTICS.MODERN_ANALYTICS.KEY_PERFORMANCE_METRICS')}>
        <div style={{ display: 'flex', gap: 10 }}>
          {kpis.map((kpi) => (
            <div
              key={kpi.label}
              style={{
                flex: 1,
                border: `1px solid ${border}`,
                borderRadius: 8,
                padding: '12px 14px',
              }}
            >
              <p style={{ fontSize: 10, color: inkSecondary, margin: '0 0 6px 0' }}>{kpi.label}</p>
              <p style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px 0' }}>{kpi.value}</p>
              <Delta change={kpi.change} />
            </div>
          ))}
        </div>
      </Section>

      {data.trends?.visits?.length > 0 && (
        <Section
          title={t('ANALYTICS.MODERN_ANALYTICS.VISITS_TREND')}
          subtitle={`${key('TOTAL')}: ${formatNumber(visitsTotal)}`}
        >
          <div data-chart="visits-trend" style={{ backgroundColor: '#FFFFFF' }}>
            <TrendChart series={data.trends.visits} gradientId="pdfVisitsGradient" />
          </div>
        </Section>
      )}

      {data.trends?.engagements?.length > 0 && (
        <Section
          title={key('ENGAGEMENTS_TREND')}
          subtitle={`${key('TOTAL')}: ${formatNumber(engagementsTrendTotal)}`}
        >
          <div data-chart="engagements-trend" style={{ backgroundColor: '#FFFFFF' }}>
            <TrendChart series={data.trends.engagements} gradientId="pdfEngagementsGradient" />
          </div>
        </Section>
      )}
    </>
  );

  // ── Page 2: engagement breakdown + user journey ─────────────────────────────
  const pageTwo = engagementTotal > 0 && (
    <>
      <Section
        title={t('ANALYTICS.MODERN_ANALYTICS.ENGAGEMENT_TYPES')}
        subtitle={`${key('TOTAL')}: ${formatNumber(engagementTotal)}`}
      >
        <div data-chart="engagement-types" style={{ backgroundColor: '#FFFFFF' }}>
        <BarChart
          width={CONTENT_W}
          height={engagementRows.length * 36 + 30}
          data={engagementRows}
          layout="vertical"
          margin={{ top: 0, right: 70, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke={border} strokeWidth={1} horizontal={false} />
          <XAxis type="number" tick={axisTick} tickLine={false} axisLine={{ stroke: border }} tickFormatter={(v: number) => v.toLocaleString()} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ ...axisTick, fill: ink }} tickLine={false} axisLine={false} width={150} />
          <Bar dataKey="value" fill={accent} barSize={18} radius={[0, 4, 4, 0]} isAnimationActive={false}>
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v: React.ReactNode) => Number(v).toLocaleString()}
              style={{ fontSize: 11, fill: ink, fontFamily }}
            />
          </Bar>
        </BarChart>
        </div>

        <table style={{ ...styles.table, marginTop: 16 }}>
          <thead>
            <tr>
              <th style={styles.th}>{key('ENGAGEMENT_TYPE')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('COUNT')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('SHARE')}</th>
            </tr>
          </thead>
          <tbody>
            {engagementRows.map((row) => (
              <tr key={row.name}>
                <td style={styles.td}>{row.name}</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(row.value)}</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>
                  {engagementTotal ? ((row.value / engagementTotal) * 100).toFixed(1) : '0.0'}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {journeyStages.length > 0 && (
        <Section title={key('USER_JOURNEY')}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{key('STAGE')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('USERS')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('CONVERSION_RATE')}</th>
              </tr>
            </thead>
            <tbody>
              {journeyStages.map((stage) => (
                <tr key={stage.stage}>
                  <td style={styles.td}>{stage.stage}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(stage.users)}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{(stage.conversionRate || 0).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </>
  );

  // ── Page 3: top products + geography ────────────────────────────────────────
  const pageThree = topProducts.length > 0 && (
    <>
      <Section title={t('ANALYTICS.MODERN_ANALYTICS.TOP_PERFORMING_PRODUCTS')}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>{key('PRODUCT')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('VIEWS')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('INTERACTIONS')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('DOWNLOADS')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('CONVERSION_RATE')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('ENGAGEMENT_RATE')}</th>
              <th style={{ ...styles.th, ...styles.thNum }}>{key('UNIQUE_USERS')}</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.map((product, index) => (
              <tr key={product.productId || index}>
                <td style={{ ...styles.td, color: inkSecondary }}>{index + 1}</td>
                <td style={{ ...styles.td, fontWeight: 500 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProductThumb src={thumbnails?.[product.productId]} name={product.name} />
                    <div>
                      <div>{product.name}</div>
                      {product.sku && (
                        <div style={{ fontSize: 9, fontWeight: 400, color: inkSecondary }}>
                          {key('SKU')}: {product.sku}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(product.metrics.totalViews)}</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(product.metrics.totalInteractions)}</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(product.metrics.totalDownloads)}</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{(product.metrics.conversionRate || 0).toFixed(1)}%</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{(product.metrics.engagementRate || 0).toFixed(1)}%</td>
                <td style={{ ...styles.td, ...styles.tdNum }}>{product.metrics.uniqueUserCount != null ? formatNumber(product.metrics.uniqueUserCount) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

    </>
  );

  // ── Page 4: geography (countries + cities) ──────────────────────────────────
  const pageFour = (topCountries.length > 0 || topCities.length > 0) && (
    <>
      {topCountries.length > 0 && (
        <Section title={key('TOP_COUNTRIES')}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{key('COUNTRY')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('UNIQUE_USERS')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('EVENTS')}</th>
              </tr>
            </thead>
            <tbody>
              {topCountries.slice(0, 10).map((country) => (
                <tr key={country.country}>
                  <td style={styles.td}>{country.country}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(country.uniqueUsers)}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(country.events)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {topCities.length > 0 && (
        <Section title={key('TOP_CITIES')}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{key('CITY')}</th>
                <th style={styles.th}>{key('COUNTRY')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('UNIQUE_USERS')}</th>
                <th style={{ ...styles.th, ...styles.thNum }}>{key('EVENTS')}</th>
              </tr>
            </thead>
            <tbody>
              {topCities.slice(0, 10).map((city) => (
                <tr key={`${city.city}-${city.country}`}>
                  <td style={styles.td}>{city.city}</td>
                  <td style={styles.td}>{city.country}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(city.uniqueUsers)}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>{formatNumber(city.events)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </>
  );

  const pages = [pageOne, pageTwo, pageThree, pageFour].filter(Boolean);

  return <div>{pages.map((content, index) => renderPage(content, index, pages.length))}</div>;
};

export default AnalyticsPdfReport;
